import React from 'react';
import { AppButton, AppMultiSelect, SearchTextField } from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { useTheme } from '@shared/providers/theme/ThemeContext';
type TenantOption = { name: string; site_type: string };

type ClinicUsersFilterPanelProps = {
  /** When true (super-admin or family admin), show サイト区分 filter. */
  canUseSiteTypeFeatures: boolean;
  tenants: TenantOption[];
  siteTypes: string[];
  hasAllSelected: boolean;
  onChangeSiteTypes: (next: string[]) => void;

  searchLoginId: string;
  onChangeSearchLoginId: (next: string) => void;
  searchName: string;
  onChangeSearchName: (next: string) => void;
  onSearch: () => void;
  onCsv: () => void;
  csvLoading?: boolean;
};

const ClinicUsersFilterPanel: React.FC<ClinicUsersFilterPanelProps> = ({
  canUseSiteTypeFeatures,
  tenants,
  siteTypes,
  hasAllSelected,
  onChangeSiteTypes,
  searchLoginId,
  onChangeSearchLoginId,
  searchName,
  onChangeSearchName,
  onSearch,
  onCsv,
  csvLoading = false,
}) => {
  const { colors } = useTheme();
  const tenantSiteTypes = tenants.map(t => t.site_type).filter(Boolean) as string[];

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div
        className={`grid gap-4 ${canUseSiteTypeFeatures ? 'grid-cols-4' : 'grid-cols-3'} pb-4`}
      >
        {canUseSiteTypeFeatures && (
          <div>
            <label htmlFor="clinic_user_site_types" className="mb-1 block text-sm text-gray-600">
              サイト区分
            </label>
            <AppMultiSelect
              id="clinic_user_site_types"
              placeholder="サイト区分"
              value={siteTypes}
              className="w-full"
              maxTagCount={hasAllSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSelected ? () => 'すべて' : undefined}
              showSearch
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={[
                { label: 'すべて', value: 'all' },
                ...tenants.map(t => ({ label: t.name, value: t.site_type })),
              ]}
              onChange={values => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeSiteTypes(tenantSiteTypes);
                  return;
                }
                onChangeSiteTypes(next.filter(v => v !== 'all'));
              }}
            />
          </div>
        )}

        <div>
          <label htmlFor="search_login_id" className="mb-1 block text-sm text-gray-600">
            ユーザーID
          </label>
          <SearchTextField
            id="search_login_id"
            value={searchLoginId}
            placeholder="ユーザーID"
            onChange={e => onChangeSearchLoginId(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onSearch();
              }
            }}
          />
        </div>

        <div>
          <label htmlFor="search_name" className="mb-1 block text-sm text-gray-600">
            治療院ユーザー名
          </label>
          <SearchTextField
            id="search_name"
            value={searchName}
            placeholder="治療院ユーザー名"
            onChange={e => onChangeSearchName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onSearch();
              }
            }}
          />
        </div>

        <div className="flex items-end gap-2">
          <AppButton
            onClick={onSearch}
            type="primary"
            icon={<Search />}
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            type="primary"
            icon={<Download />}
            disabled={csvLoading}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
            className="py-1 px-4 text-white h-auto btn-ra2-hover font-medium !h-[40px]"
          >
            {csvLoading ? '出力中...' : 'CSV'}
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default ClinicUsersFilterPanel;
